<template>
  <!-- Add / Edit Leaves -->
  <div class="modal fade dq-leave-modal" id="add_leaves">
    <div class="modal-dialog modal-dialog-centered modal-lg dq-leave-dialog">
      <div class="modal-content dq-modal-content" :key="modalKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-modal-titlewrap">
            <div class="dq-hero-icon dq-hero-green">
              <i class="ti ti-calendar-time"></i>
            </div>
            <div>
              <h4 class="modal-title dq-modal-title mb-0">
                {{ editingId ? "Edit Leave" : "Apply for Leave" }}
              </h4>
              <div class="dq-modal-sub">
                Submit your leave request for approval.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <!-- Body -->
        <form @submit.prevent="submit">
          <div class="modal-body dq-modal-body pb-0">
            <!-- LEAVE TYPE -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-green">
                    <i class="ti ti-briefcase"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Leave Type</div>
                    <div class="dq-help">
                      Select the type of leave you want to apply for.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <label class="form-label dq-label">Leave Type</label>
                <select
                  v-model="form.leave_type_id"
                  class="form-select dq-input"
                >
                  <option value="">Select leave type</option>
                  <option v-for="t in typeOptions()" :key="t.id" :value="t.id">
                    {{ t.label }}
                  </option>
                </select>

                <div v-if="form.leave_type_id" class="dq-inline-stats mt-2">
                  <div class="dq-stat">
                    <span class="dq-stat-label">Remaining</span>
                    <span class="dq-stat-value">
                      {{ remainingForType }} day(s)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- DATES -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-blue">
                    <i class="ti ti-calendar"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Leave Duration</div>
                    <div class="dq-help">Choose the start and end dates.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label dq-label">From</label>
                    <div class="input-icon-end position-relative">
                      <date-picker
                        v-model="form.from_date"
                        class="form-control dq-input datetimepicker"
                        placeholder="dd/mm/yyyy"
                        :editable="true"
                        :clearable="false"
                        :input-format="dateFormat"
                      />
                      <span class="input-icon-addon">
                        <i class="ti ti-calendar text-gray-7"></i>
                      </span>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">To</label>
                    <div class="input-icon-end position-relative">
                      <date-picker
                        v-model="form.to_date"
                        class="form-control dq-input datetimepicker"
                        placeholder="dd/mm/yyyy"
                        :editable="!form.half_day"
                        :clearable="false"
                        :input-format="dateFormat"
                        :disabled="form.half_day"
                      />
                      <span class="input-icon-addon">
                        <i class="ti ti-calendar text-gray-7"></i>
                      </span>
                    </div>
                  </div>
                </div>

                <div class="form-check mt-2">
                  <input
                    id="employee_half_day"
                    v-model="form.half_day"
                    class="form-check-input"
                    type="checkbox"
                    @change="onHalfDayToggle"
                  />
                  <label class="form-check-label" for="employee_half_day">
                    Half-day leave (same from/to date)
                  </label>
                </div>

                <div class="row g-3 mt-1">
                  <div class="col-md-6">
                    <div class="dq-readonly-card">
                      <span class="dq-readonly-label">Total Days</span>
                      <span class="dq-readonly-value">
                        {{ formatDaysLabel(form.days) }}
                      </span>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="dq-readonly-card dq-muted-card">
                      <span class="dq-readonly-label"> Remaining Balance </span>
                      <span class="dq-readonly-value">
                        {{ form.remaining_days }} day(s)
                      </span>
                    </div>
                  </div>
                </div>

                <div v-if="startsInPast" class="alert alert-warning mt-3 dq-alert">
                  Leave cannot start in the past. Ask HR to record dates that have already passed.
                </div>
                <div v-else-if="beforeJoining" class="alert alert-warning mt-3 dq-alert">
                  Leave cannot start before your joining date ({{ joiningDate }}).
                </div>
                <div v-else-if="crossesYear" class="alert alert-warning mt-3 dq-alert">
                  Leave cannot cross into a new year. Submit one request ending 31 Dec and another
                  starting 1 Jan.
                </div>
                <div
                  v-else-if="
                    form.days > form.remaining_days && form.remaining_days > 0
                  "
                  class="alert alert-danger mt-3 dq-alert"
                >
                  You are requesting more days than your remaining balance.
                </div>
              </div>
            </div>

            <!-- COVER -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-blue">
                    <i class="ti ti-user-share"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Who will cover your work (optional)</div>
                    <div class="dq-help">
                      Choose the colleague who will handle your duties while you are away.
                    </div>
                  </div>
                </div>
              </div>
              <div class="dq-section-body">
                <div class="dropdown w-100 dq-emp-dd">
                  <button
                    class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center dq-dd-btn"
                    type="button"
                    data-bs-toggle="dropdown"
                    data-bs-display="static"
                    @click="onCoverDropdownOpen"
                  >
                    <span class="text-truncate">{{ selectedCoverLabel }}</span>
                    <i class="ti ti-chevron-down ms-2"></i>
                  </button>
                  <div class="dropdown-menu p-2 w-100 shadow dq-dd-menu" @click.stop>
                    <input
                      ref="coverSearchRef"
                      v-model="coverQuery"
                      type="text"
                      class="form-control mb-2 dq-input"
                      placeholder="Search employee name or ID..."
                      autocomplete="off"
                    />
                    <div class="dq-search-list">
                      <button
                        v-for="person in filteredColleagues"
                        :key="person.id"
                        type="button"
                        class="dropdown-item text-truncate dq-dd-item"
                        @click="chooseCover(person)"
                      >
                        <div class="fw-semibold text-truncate">{{ coverOptionText(person) }}</div>
                      </button>
                      <div
                        v-if="filteredColleagues.length === 0"
                        class="text-center py-2 small text-muted"
                      >
                        No employee found
                      </div>
                    </div>
                    <div class="d-flex justify-content-between align-items-center pt-2 border-top mt-2">
                      <button type="button" class="btn btn-sm btn-light" @click="clearCover">
                        Clear
                      </button>
                      <span class="small text-muted">{{ filteredColleagues.length }} result(s)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- REASON -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-purple">
                    <i class="ti ti-message"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Reason (Optional)</div>
                    <div class="dq-help">
                      Provide additional context if needed.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <textarea
                  v-model="form.reason"
                  class="form-control dq-input"
                  rows="3"
                  placeholder="Optional explanation for your leave request"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn-muted me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary dq-btn-primary"
              :disabled="form.days === 0 || form.days > form.remaining_days || crossesYear || startsInPast || beforeJoining"
            >
              <span
                v-if="applyingLeave"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ editingId ? "Save Changes" : "Submit Leave Request" }}
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Confirm -->
  <div id="employee_leave_delete_modal" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content">
        <div class="modal-header border-0 pb-0">
          <div class="d-flex align-items-center gap-3">
            <span
              class="avatar avatar-md bg-danger-transparent text-danger rounded-circle d-inline-flex align-items-center justify-content-center"
            >
              <i class="ti ti-trash fs-18"></i>
            </span>
            <div>
              <h4 class="modal-title mb-0">{{ deleteTitle }}</h4>
              <p class="text-muted mb-0 fs-13">{{ deleteHelp }}</p>
            </div>
          </div>
          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>
        <div class="modal-body pt-3">
          <div class="alert alert-danger d-flex align-items-start gap-2 mb-0">
            <i class="ti ti-alert-triangle mt-1"></i>
            <div>
              <div class="fw-medium">{{ deleteQuestion }}</div>
              <div class="fs-13" v-if="deleteTarget">
                {{ deleteTarget.leave_type?.name || "Leave" }} —
                {{ formatDeleteDate(deleteTarget.from_date) }} to
                {{ formatDeleteDate(deleteTarget.to_date) }}
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer border-0 pt-0">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
            {{ deleteAction }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import * as bootstrap from "bootstrap";
import moment from "moment";
import { useEmployeeLeaveStore } from "@/stores/employeeLeaveStore";
import employeeLeaveService from "@/services/hrm/employeeLeaveService";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/utils/toast";

const store = useEmployeeLeaveStore();
const auth = useAuthStore();

const form = ref(blank());
const editingId = ref(null);
const modalKey = ref(0);
const deleteTarget = ref(null);
const colleagues = ref([]);
const coverQuery = ref("");
const coverSearchRef = ref(null);
const deleting = ref(false);
const dateFormat = "dd/MM/yyyy";

const applyingLeave = computed(() => store.loading);

function blank() {
  return {
    leave_type_id: "",
    from_date: null,
    to_date: null,
    days: 0,
    remaining_days: 0,
    half_day: false,
    reason: "",
    delegate_employee_id: null,
  };
}
function reset() {
  const b = blank();
  for (const k in b) form.value[k] = b[k];
  editingId.value = null;
  coverQuery.value = "";
}

function coverOptionText(person) {
  const name = person?.name || "Employee";
  return person?.employee_id ? `${name} (${person.employee_id})` : name;
}

const selectedCoverLabel = computed(() => {
  const id = Number(form.value.delegate_employee_id || 0);
  if (!id) return "Search & select employee";
  const match = colleagues.value.find((person) => Number(person.id) === id);
  return match ? coverOptionText(match) : "Selected employee";
});

const filteredColleagues = computed(() => {
  const q = coverQuery.value.trim().toLowerCase();
  if (!q) return colleagues.value;
  return colleagues.value.filter((person) => {
    const text = coverOptionText(person).toLowerCase();
    return text.includes(q) || String(person.id).includes(q);
  });
});

function chooseCover(person) {
  form.value.delegate_employee_id = person.id;
  coverQuery.value = "";
  document.body.click();
}

function clearCover() {
  form.value.delegate_employee_id = null;
  coverQuery.value = "";
}

function onCoverDropdownOpen() {
  nextTick(() => coverSearchRef.value?.focus());
}

function toYMD(d) {
  if (!d) return null;
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function diffDays(a, b, halfDay = false) {
  if (!a || !b) return 0;
  if (halfDay) {
    const start = new Date(a);
    const end = new Date(b);
    if (start.toDateString() !== end.toDateString()) return 0;
    const day = start.getDay();
    if (day === 0 || day === 6) return 0;
    return 0.5;
  }
  const start = new Date(a);
  const end = new Date(b);
  if (end < start) return 0;
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

function formatDaysLabel(days) {
  const n = Number(days ?? 0);
  return `${Number.isInteger(n) ? n : n.toFixed(1)} day(s)`;
}

function onHalfDayToggle() {
  if (form.value.half_day && form.value.from_date) {
    form.value.to_date = form.value.from_date;
  }
}

function formatDeleteDate(d) {
  return d ? moment(d).format("DD MMM YYYY") : "-";
}

function openEdit(record) {
  if (!record) return;
  editingId.value = record.id;
  form.value.leave_type_id = record.leave_type_id;
  form.value.from_date = new Date(record.from_date);
  form.value.to_date = new Date(record.to_date);
  form.value.half_day = !!record.half_day;
  form.value.reason = record.reason || "";
  form.value.delegate_employee_id = record.delegate_employee_id || null;
  modalKey.value++;
  bootstrap.Modal.getOrCreateInstance(document.getElementById("add_leaves")).show();
}

function openDelete(record) {
  deleteTarget.value = record;
  bootstrap.Modal.getOrCreateInstance(
    document.getElementById("employee_leave_delete_modal")
  ).show();
}

async function confirmDelete() {
  if (!deleteTarget.value?.id) return;
  deleting.value = true;
  try {
    await store.remove(deleteTarget.value.id);
    bootstrap.Modal.getInstance(
      document.getElementById("employee_leave_delete_modal")
    )?.hide();
    deleteTarget.value = null;
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to delete leave", "error");
  } finally {
    deleting.value = false;
  }
}

defineExpose({ openEdit, openDelete });

const joiningDate = computed(() =>
  String(auth.user?.employee?.company_doj || auth.user?.company_doj || "").slice(0, 10)
);

function ymdOf(value) {
  return toYMD(value);
}

function todayYmd() {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${m}-${d}`;
}

const startsInPast = computed(() => {
  const from = ymdOf(form.value.from_date);
  return !!from && from < todayYmd();
});

const beforeJoining = computed(() => {
  const from = ymdOf(form.value.from_date);
  return !!from && !!joiningDate.value && from < joiningDate.value;
});

const deleteTitle = computed(() => {
  if (deleteTarget.value?.status === "Approved") return "Cancel Approved Leave";
  if (deleteTarget.value?.status === "ManagerApproved") return "Withdraw Leave Request";
  return "Delete Leave Request";
});

const deleteHelp = computed(() => {
  if (deleteTarget.value?.status === "Approved") {
    return "This leave has not started. Cancelling tells HR and puts the days back on your balance.";
  }
  if (deleteTarget.value?.status === "ManagerApproved") {
    return "Your manager already recommended this. Withdrawing sends the days back to your balance.";
  }
  return "Only requests still waiting for approval can be removed.";
});

const deleteQuestion = computed(() => {
  if (deleteTarget.value?.status === "Approved") return "Cancel this approved leave?";
  if (deleteTarget.value?.status === "ManagerApproved") return "Withdraw this leave request?";
  return "Delete this leave request?";
});

const deleteAction = computed(() => {
  if (deleteTarget.value?.status === "Approved") return "Cancel leave";
  if (deleteTarget.value?.status === "ManagerApproved") return "Withdraw";
  return "Delete";
});

const crossesYear = computed(() => {
  const { from_date, to_date, half_day } = form.value;
  if (!from_date || !to_date || half_day) return false;
  return new Date(from_date).getFullYear() !== new Date(to_date).getFullYear();
});

const remainingForType = computed(() => {
  if (!form.value.leave_type_id) return 0;
  const t = store.summary.types.find(
    (x) => x.leave_type_id === Number(form.value.leave_type_id)
  );
  return t?.remaining ?? 0;
});

watch(
  () => [form.value.leave_type_id, form.value.from_date, form.value.to_date, form.value.half_day],
  () => {
    form.value.days = diffDays(
      form.value.from_date,
      form.value.to_date,
      form.value.half_day
    );
    form.value.remaining_days = remainingForType.value;
  },
  { deep: true }
);

function typeOptions() {
  return (store.summary?.types || []).map((t) => ({
    id: t.leave_type_id,
    label: t.name,
    slug: t.slug,
  }));
}

async function submit() {
  if (!form.value.leave_type_id)
    return toast("Leave type is required", "error");
  if (!form.value.from_date || !form.value.to_date)
    return toast("From/To dates are required", "error");
  if (startsInPast.value)
    return toast("Leave cannot start in the past. Ask HR if these dates need to be recorded.", "error");
  if (beforeJoining.value)
    return toast(`Leave cannot start before your joining date (${joiningDate.value}).`, "error");

  const payload = {
    leave_type_id: Number(form.value.leave_type_id),
    from_date: toYMD(form.value.from_date),
    to_date: toYMD(form.value.half_day ? form.value.from_date : form.value.to_date),
    half_day: form.value.half_day,
    reason: form.value.reason?.trim() || null,
    delegate_employee_id: form.value.delegate_employee_id
      ? Number(form.value.delegate_employee_id)
      : null,
  };

  if (form.value.days > remainingForType.value) {
    return toast(
      `You cannot apply for ${form.value.days} day(s). Remaining is ${remainingForType.value}.`,
      "error"
    );
  }

  if (editingId.value) {
    await store.update(editingId.value, payload);
  } else {
    await store.create(payload);
  }
  close();
}

function close() {
  document.querySelector('#add_leaves [data-bs-dismiss="modal"]')?.click();
}

let cleanup = null;
onMounted(async () => {
  try {
    const { data } = await employeeLeaveService.colleagues();
    colleagues.value = Array.isArray(data) ? data : [];
  } catch {
    colleagues.value = [];
  }
  const addEl = document.getElementById("add_leaves");
  const handleShow = () => {
    if (!editingId.value) reset();
    form.value.remaining_days = remainingForType.value;
    modalKey.value++;
  };
  const handleHidden = () => reset();

  addEl?.addEventListener("show.bs.modal", handleShow);
  addEl?.addEventListener("hidden.bs.modal", handleHidden);

  cleanup = () => {
    addEl?.removeEventListener("show.bs.modal", handleShow);
    addEl?.removeEventListener("hidden.bs.modal", handleHidden);
  };
});

onBeforeUnmount(() => cleanup && cleanup());
</script>

<style scoped>
/* ===============================
   MODAL WIDTH & CONTAINER
================================ */
.dq-leave-dialog {
  max-width: 880px; /* desktop sweet spot */
}

@media (max-width: 768px) {
  .dq-leave-dialog {
    max-width: 95%;
  }
}

.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 80px rgba(15, 23, 42, 0.12);
}

/* ===============================
   HEADER
================================ */
.dq-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(
    180deg,
    rgba(34, 197, 94, 0.08),
    rgba(255, 255, 255, 1)
  );
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.dq-modal-titlewrap {
  display: flex;
  gap: 14px;
  align-items: center;
}

.dq-modal-title {
  font-weight: 800;
  letter-spacing: -0.3px;
}

.dq-modal-sub {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Hero icon */
.dq-hero-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.dq-hero-green {
  background: rgba(34, 197, 94, 0.14);
  color: rgb(22, 163, 74);
}

/* ===============================
   BODY
================================ */
.dq-modal-body {
  padding: 22px 24px;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

/* ===============================
   SECTIONS
================================ */
.dq-section {
  background: #fff;
  border-radius: 18px;
  padding: 18px;
  margin-bottom: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
}

.dq-section-head {
  margin-bottom: 14px;
}

.dq-section-title {
  display: flex;
  gap: 12px;
  align-items: center;
}

.dq-section-badge {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dq-badge-green {
  background: rgba(34, 197, 94, 0.12);
  color: rgb(22, 163, 74);
}

.dq-badge-blue {
  background: rgba(59, 130, 246, 0.12);
  color: rgb(37, 99, 235);
}

.dq-badge-purple {
  background: rgba(139, 92, 246, 0.12);
  color: rgb(109, 40, 217);
}

.dq-h6 {
  font-weight: 700;
}

.dq-help {
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
}

/* ===============================
   INPUTS
================================ */
.dq-label {
  font-size: 0.8rem;
  font-weight: 600;
}

.dq-input {
  border-radius: 12px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}

.dq-input:focus {
  border-color: rgba(34, 197, 94, 0.6);
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.15);
}

/* ===============================
   READ-ONLY CARDS
================================ */
.dq-readonly-card {
  padding: 14px;
  border-radius: 16px;
  background: rgba(34, 197, 94, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dq-muted-card {
  background: rgba(15, 23, 42, 0.04);
}

.dq-readonly-label {
  font-size: 0.75rem;
  color: rgba(15, 23, 42, 0.55);
}

.dq-readonly-value {
  font-weight: 900;
  font-size: 1.05rem;
}

/* ===============================
   FOOTER
================================ */
.dq-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}

.dq-btn-primary {
  border-radius: 12px;
  padding: 10px 18px;
  font-weight: 700;
}

.dq-btn-muted {
  border-radius: 12px;
}

/* ===============================
   ALERT
================================ */
.dq-alert {
  border-radius: 12px;
  font-size: 0.85rem;
}

.dq-emp-dd .dq-dd-menu {
  position: static;
  display: none;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  transform: none !important;
}
.dq-emp-dd .dq-dd-menu.show {
  display: block;
}
.dq-search-list {
  max-height: 260px;
  overflow-y: auto;
}
.dq-dd-item {
  border-radius: 10px;
  padding: 10px;
}
.dq-dd-btn {
  border-radius: 12px;
  min-height: 42px;
}
</style>
